import { resolveDecisionEventWindow } from './eventStartsWithinWindow'

export interface DecisionEventQuery {
  starts_after: string
  starts_before: string
  order: 'asc'
  per_page: number
}

/**
 * Start of `now`'s LOCAL day, as an ISO instant.
 *
 * Local, not UTC, because that is the boundary `resolveDefaultScheduleTab`
 * compares against — it calls `setHours(0, 0, 0, 0)`. A UTC midnight bound
 * would ask the server a different question than the classifier then answers,
 * and the two would disagree for anyone west of Greenwich in the evening.
 */
const startOfLocalDay = (now: Date): string => {
  const start = new Date(now)
  start.setHours(0, 0, 0, 0)
  return start.toISOString()
}

/**
 * The opening-tab decision, as a query: the single earliest event from the
 * start of today onward.
 *
 * `resolveDefaultScheduleTab` consults events for exactly one purpose — to
 * upgrade a games-only PAST to TODAY or UPCOMING. A PAST event can produce
 * neither, so the backward half of `resolveDecisionEventWindow`'s ±6 months was
 * always fetched and discarded. Starting at today makes the request answer the
 * question actually being asked.
 *
 * `per_page: 1` follows from that: the EARLIEST row from today onward settles
 * it. If it falls today the tab is TODAY, if later it is UPCOMING, and if there
 * is no row at all it is PAST. No later row can change any of those, so one row
 * is not an approximation — it is the whole answer.
 *
 * The upper bound is taken from `resolveDecisionEventWindow` rather than
 * recomputed, so the two cannot drift on where "far enough ahead" ends.
 *
 * **Day-quantized, deliberately.** This becomes part of a react-query key and
 * is computed during render, so a second- or millisecond-precision value yields
 * a new key on every render — which refetches, which re-renders, which
 * refetches, without end.
 */
export const resolveDecisionEventQuery = (
  now: Date = new Date()
): DecisionEventQuery => ({
  starts_after: startOfLocalDay(now),
  starts_before: resolveDecisionEventWindow(now).startsBefore,
  order: 'asc',
  per_page: 1,
})
